import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Checkbox,
  TextField,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import { db } from '../../../auth/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import './ParticipantSelectionModal.css';

function ParticipantSelectionModal({ open, onClose, selectedUsers, onUpdateSelection }) {
  // # State for search functionality
  const [searchQuery, setSearchQuery] = useState('');
  // # State for all users
  const [users, setUsers] = useState([]);
  // # State for loading
  const [loading, setLoading] = useState(true);
  // # State for error
  const [error, setError] = useState(null);

  // # Fetch users from Firestore
  useEffect(() => {
    const fetchUsers = async () => {
      if (!open) return; // # Only fetch when modal is open
      
      try {
        setLoading(true);
        setError(null);

        const usersRef = collection(db, 'users');
        // # Fetch all users first
        const querySnapshot = await getDocs(usersRef);
        
        console.log(`Fetched ${querySnapshot.docs.length} total users`);
        
        const usersData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          
          // # Debug info for each user
          console.log(`User ${doc.id}: status=${data.status}, isProfileComplete=${data.isProfileComplete}`);
          
          // # Skip users who are explicitly marked as inactive or suspended
          if (data.status === 'inactive' || data.status === 'suspended') {
            console.log(`Skipping user ${doc.id} with status: ${data.status}`);
            return null;
          }
          
          // # Always include users with completed profiles
          if (data.isProfileComplete) {
            console.log(`Including user ${doc.id} with isProfileComplete=true`);
          }
          // # Include active users
          else if (data.status === 'active') {
            console.log(`Including user ${doc.id} with status=active`);
          }
          // # Skip users without a completed profile or active status
          else {
            console.log(`Skipping user ${doc.id}: not active and profile not complete`);
            return null;
          }
          
          return {
            ...data,
            uid: doc.id,
            firstName: data.firstName || '',
            middleName: data.middleName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            // # Create displayName from name parts
            displayName: formatName({
              firstName: data.firstName || '',
              middleName: data.middleName || '',
              lastName: data.lastName || '',
              email: data.email || ''
            }),
            identifier: data.username ? `@${data.username}` : (data.email || doc.id),
          };
        }).filter(Boolean); // # Remove null entries (inactive/suspended users)

        console.log(`Including ${usersData.length} active/complete users after filtering`);
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [open]);

  // # Format name helper function
  const formatName = (user) => {
    if (!user) return 'N/A';
    
    const parts = [
      user.firstName,
      user.middleName,
      user.lastName
    ].filter(Boolean);
    
    if (parts.length > 0) {
      return parts.join(' ');
    }
    
    return user.email || 'N/A';
  };

  // # Filter users based on search query
  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.displayName?.toLowerCase().includes(searchLower) ||
      user.identifier?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  // # Handle user selection
  const handleToggleUser = (user) => {
    const isSelected = selectedUsers.some(u => u.uid === user.uid);
    const newSelection = isSelected
      ? selectedUsers.filter(u => u.uid !== user.uid)
      : [...selectedUsers, user];
    onUpdateSelection(newSelection);
  };

  // # Handle select all users
  const handleSelectAll = () => {
    const newSelection = filteredUsers.length === selectedUsers.length ? [] : filteredUsers;
    onUpdateSelection(newSelection);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="participant_selection_modal"
    >
      <Box className="participant_selection_content">
        {/* # Header with close button */}
        <div className="participant_selection_header">
          <Typography variant="h6" className="participant_selection_title">
            Select Participants
          </Typography>
          <IconButton onClick={onClose} className="participant_close_btn">
            <Close />
          </IconButton>
        </div>

        {/* # Search Input */}
        <TextField
          fullWidth
          placeholder="Search participants..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search />,
          }}
          className="participant_search_input"
        />

        {/* # Error Message */}
        {error && (
          <Typography color="error" className="participant_error_message">
            {error}
          </Typography>
        )}

        {/* # Loading State */}
        {loading ? (
          <Box className="participant_loading">
            <CircularProgress size={40} />
            <Typography>Loading participants...</Typography>
          </Box>
        ) : (
          <List className="participant_list">
            {/* # Select All Option */}
            {filteredUsers.length > 0 && (
              <ListItem
                onClick={handleSelectAll}
                className="participant_list_item participant_select_all"
                sx={{ cursor: 'pointer' }}
              >
                <ListItemText primary="Select All" />
                <Checkbox
                  checked={filteredUsers.length > 0 && filteredUsers.length === selectedUsers.length}
                  indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                  className="participant_checkbox"
                />
              </ListItem>
            )}
            
            {filteredUsers.map((user) => (
              <ListItem
                key={user.uid}
                onClick={() => handleToggleUser(user)}
                className="participant_list_item"
                sx={{ cursor: 'pointer' }}
              >
                <ListItemAvatar>
                  <Avatar src={user.photoURL || null}>
                    {!user.photoURL && (user.displayName?.[0] || user.email?.[0] || '?')}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.displayName || 'Unnamed User'}
                  secondary={user.email || user.identifier}
                />
                <Checkbox
                  checked={selectedUsers.some(u => u.uid === user.uid)}
                  className="participant_checkbox"
                />
              </ListItem>
            ))}
            {filteredUsers.length === 0 && (
              <Typography className="no_results_message">
                No participants found
              </Typography>
            )}
          </List>
        )}
      </Box>
    </Modal>
  );
}

export default ParticipantSelectionModal; 